import { isly } from "isly";
import { Date } from "../Date";
import { system } from "../system";
import { Time } from "../Time";
import { TimeZone } from "../TimeZone";
import { Numeric as DateTimeNumeric } from "./Numeric";
export var DateTime;
(function (DateTime) {
    var _a;
    DateTime.Numeric = DateTimeNumeric;
    _a = isly
        .string((value) => {
        const { date, time, timeZoneOffset } = DateTime.split(value);
        return Date.is(date) && Time.type.optional().is(time) && TimeZone.Offset.type.optional().is(timeZoneOffset);
    }, "YYYY-MM-DDTHH[:mm[:ss[.fff]]][Z|(+|-)HH:MM]")
        .rename("isoly.DateTime")
        .describe("Valid ISO 8601 date-time formats include YYYY-MM-DDTHH[:mm[:ss[.fff]]][Z|(+|-)HH:MM].")
        .bind(), DateTime.type = _a.type, DateTime.is = _a.is, DateTime.flawed = _a.flawed;
    function split(value) {
        const [date, splitted] = value.split("T", 2);
        const [time, timeZoneOffset] = (splitted?.split(/(Z|[+-].{5})?$/, 2) ?? [undefined, undefined]);
        return {
            date,
            time,
            timeZoneOffset,
        };
    }
    DateTime.split = split;
    function parse(value) {
        return new system.Date(DateTime.truncate(value, "milliseconds"));
    }
    DateTime.parse = parse;
    function create(value, resolution = "seconds") {
        if (typeof value == "number") {
            switch (resolution) {
                case "days":
                    value = value * 24;
                case "hours":
                    value = value * 60;
                case "minutes":
                    value = value * 60;
                case "seconds":
                    value = value * 1000;
                case "milliseconds":
            }
            value = new system.Date(value);
        }
        return fix(value.toISOString());
    }
    DateTime.create = create;
    function normalize(value, precision) {
        return DateTime.Numeric.format(DateTime.Numeric.parse(value), precision);
    }
    DateTime.normalize = normalize;
    function fix(value) {
        if (value.length == 22 && value.match(/\.\dZ$/))
            value = value.substring(0, 21) + "00Z";
        else if (value.length == 23 && value.match(/\.\d\dZ$/))
            value = value.substring(0, 22) + "0Z";
        return value;
    }
    DateTime.fix = fix;
    DateTime.fixIncorrect = fix;
    function fromLocal(local, timeZone) {
        local = local.replace(/(Z|([+-].{5}))?$/, "");
        const utcDateTime = new system.Date(`${local}Z`);
        const localDate = new system.Date(utcDateTime.toLocaleString("sv-SE", { timeZone: timeZone }).replace(" ", "T") + "Z");
        const diffInMinutes = (localDate.getTime() - utcDateTime.getTime()) / 60000;
        const offsetHours = Math.floor(Math.abs(diffInMinutes) / 60)
            .toString()
            .padStart(2, "0");
        const offsetMinutes = (Math.abs(diffInMinutes) % 60).toString().padStart(2, "0");
        const timeZoneString = `${diffInMinutes >= 0 ? "+" : "-"}${offsetHours}:${offsetMinutes}`;
        return `${local}${timeZoneString}`;
    }
    DateTime.fromLocal = fromLocal;
    DateTime.fromLocalDateTime = fromLocal;
    function now() {
        return create(new system.Date());
    }
    DateTime.now = now;
    function localize(value, formatOrLocale, localeOrTimeZone) {
        let result;
        if (typeof formatOrLocale == "object") {
            const localeString = localeOrTimeZone ? localeOrTimeZone : Intl.DateTimeFormat().resolvedOptions().locale;
            result = (DateTime.is(value) ? parse(value) : value)
                .toLocaleString(localeString, formatOrLocale)
                .replaceAll(" ", " ");
        }
        else {
            const precision = DateTime.is(value) ? DateTime.precision(value) : "milliseconds";
            result = localize(value, {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: precision == "minutes" || precision == "seconds" || precision == "milliseconds" ? "2-digit" : undefined,
                second: precision == "seconds" || precision == "milliseconds" ? "2-digit" : undefined,
                timeZone: localeOrTimeZone,
            }, formatOrLocale);
        }
        return result;
    }
    DateTime.localize = localize;
    function startOfDay(value) {
        return value.slice(0, 10) + "T00:00:00.000" + (DateTime.is(value) ? timeZoneOffset(value) || "Z" : "Z");
    }
    DateTime.startOfDay = startOfDay;
    function endOfDay(value) {
        return value.slice(0, 10) + "T23:59:59.999" + (DateTime.is(value) ? timeZoneOffset(value) || "Z" : "Z");
    }
    DateTime.endOfDay = endOfDay;
    function timeZoneOffset(value) {
        const result = value[value.length - 1] == "Z" ? "Z" : value.substring(value.length - 6);
        return TimeZone.Offset.is(result) ? result : "";
    }
    DateTime.timeZoneOffset = timeZoneOffset;
    DateTime.timeZone = timeZoneOffset;
    function timeZoneShort(value) {
        return parse(value).getTimezoneOffset();
    }
    DateTime.timeZoneShort = timeZoneShort;
    function precision(value) {
        const zone = timeZoneOffset(value);
        const time = value.substring(0, value.length - zone.length).split("T", 2)[1];
        let result;
        switch (time.length) {
            case 2:
                result = "hours";
                break;
            case 5:
                result = "minutes";
                break;
            case 8:
                result = "seconds";
                break;
            default:
            case 12:
                result = "milliseconds";
                break;
        }
        return result;
    }
    DateTime.precision = precision;
    function truncate(value, precision) {
        const zone = timeZoneOffset(value);
        let [date, time] = value.split("T", 2);
        time = time.substring(0, time.length - zone.length);
        switch (time.length) {
            case 2:
                time += ":00:00.000";
                break;
            case 5:
                time += ":00.000";
                break;
            case 8:
                time += ".000";
                break;
        }
        let result;
        switch (precision) {
            case "hours":
                result = time.substring(0, 2);
                break;
            case "minutes":
                result = time.substring(0, 5);
                break;
            case "seconds":
                result = time.substring(0, 8);
                break;
            case "milliseconds":
                result = time.substring(0, 12);
                break;
        }
        return date + "T" + result + zone;
    }
    DateTime.truncate = truncate;
    function epoch(value, resolution = "seconds") {
        let result = (typeof value == "string" ? parse(value) : value).getTime();
        switch (resolution) {
            case "days":
                result = Math.round(result / 24);
            case "hours":
                result = Math.round(result / 60);
            case "minutes":
                result = Math.round(result / 60);
            case "seconds":
                result = Math.round(result / 1000);
            case "milliseconds":
        }
        return result;
    }
    DateTime.epoch = epoch;
    function next(time, span = 1) {
        let result;
        if (typeof span == "number")
            result = nextSecond(time, span);
        else {
            result = time;
            if (span.years)
                result = nextYear(result, span.years);
            if (span.months)
                result = nextMonth(result, span.months);
            if (span.days)
                result = nextDay(result, span.days);
            if (span.hours)
                result = nextHour(result, span.hours);
            if (span.minutes)
                result = nextMinute(result, span.minutes);
            if (span.seconds)
                result = nextSecond(result, span.seconds);
            if (span.milliseconds)
                result = nextMillisecond(result, span.milliseconds);
        }
        return result;
    }
    DateTime.next = next;
    function previous(time, span = 1) {
        let result;
        if (typeof span == "number")
            result = previousSecond(time, span);
        else {
            result = time;
            if (span.years)
                result = previousYear(result, span.years);
            if (span.months)
                result = previousMonth(result, span.months);
            if (span.days)
                result = previousDay(result, span.days);
            if (span.hours)
                result = previousHour(result, span.hours);
            if (span.minutes)
                result = previousMinute(result, span.minutes);
            if (span.seconds)
                result = previousSecond(result, span.seconds);
            if (span.milliseconds)
                result = previousMillisecond(result, span.milliseconds);
        }
        return result;
    }
    DateTime.previous = previous;
    function nextMillisecond(time, milliseconds = 1) {
        const result = parse(time);
        result.setUTCMilliseconds(result.getUTCMilliseconds() + milliseconds);
        return DateTime.create(result);
    }
    DateTime.nextMillisecond = nextMillisecond;
    function previousMillisecond(time, milliseconds = 1) {
        return nextMillisecond(time, -milliseconds);
    }
    DateTime.previousMillisecond = previousMillisecond;
    function nextSecond(time, seconds = 1) {
        const result = parse(time);
        result.setUTCSeconds(result.getUTCSeconds() + seconds);
        return DateTime.create(result);
    }
    DateTime.nextSecond = nextSecond;
    function previousSecond(time, seconds = 1) {
        return nextSecond(time, -seconds);
    }
    DateTime.previousSecond = previousSecond;
    function nextMinute(time, minutes = 1) {
        const result = parse(time);
        result.setUTCMinutes(result.getUTCMinutes() + minutes);
        return DateTime.create(result);
    }
    DateTime.nextMinute = nextMinute;
    function previousMinute(time, minutes = 1) {
        return nextMinute(time, -minutes);
    }
    DateTime.previousMinute = previousMinute;
    function nextHour(time, hours = 1) {
        const result = parse(time);
        result.setUTCHours(result.getUTCHours() + hours);
        return DateTime.create(result);
    }
    DateTime.nextHour = nextHour;
    function previousHour(time, hours = 1) {
        return nextHour(time, -hours);
    }
    DateTime.previousHour = previousHour;
    function nextDay(time, days = 1) {
        const result = parse(time);
        result.setUTCDate(result.getUTCDate() + days);
        result.setUTCMinutes(result.getUTCMinutes());
        return DateTime.create(result);
    }
    DateTime.nextDay = nextDay;
    function previousDay(time, days = 1) {
        return nextDay(time, -days);
    }
    DateTime.previousDay = previousDay;
    function nextMonth(time, months = 1) {
        const result = parse(time);
        result.setUTCMonth(result.getUTCMonth() + months);
        result.setUTCMinutes(result.getUTCMinutes());
        return DateTime.create(result);
    }
    DateTime.nextMonth = nextMonth;
    function previousMonth(time, months = 1) {
        return nextMonth(time, -months);
    }
    DateTime.previousMonth = previousMonth;
    function nextYear(time, years = 1) {
        const result = parse(time);
        result.setUTCFullYear(result.getUTCFullYear() + years);
        return DateTime.create(result);
    }
    DateTime.nextYear = nextYear;
    function previousYear(time, years = 1) {
        return nextYear(time, -years);
    }
    DateTime.previousYear = previousYear;
    function getDate(time) {
        return time.substring(0, 10);
    }
    DateTime.getDate = getDate;
    function getTime(time) {
        return time.substring(11);
    }
    DateTime.getTime = getTime;
    function getYear(time, options) {
        return options?.digits != 2 ? Number.parseInt(time.substring(0, 4)) : +getYear(time).toString().slice(-2);
    }
    DateTime.getYear = getYear;
    function getMonth(time) {
        return Number.parseInt(time.substring(5, 7));
    }
    DateTime.getMonth = getMonth;
    function getDay(time) {
        return Number.parseInt(time.substring(8, 10));
    }
    DateTime.getDay = getDay;
    function getHour(time) {
        return Number.parseInt(time.substring(11, 13));
    }
    DateTime.getHour = getHour;
    function getMinute(time) {
        return Number.parseInt(time.substring(14, 16));
    }
    DateTime.getMinute = getMinute;
    function getSecond(time) {
        return Number.parseInt(time.substring(17, 19));
    }
    DateTime.getSecond = getSecond;
    function getMillisecond(time) {
        return Number.parseInt(time.substring(20, 23));
    }
    DateTime.getMillisecond = getMillisecond;
    function span(time, relative, greatestUnit = "years") {
        let result;
        if (greatestUnit == "years") {
            result = {
                ...Date.span(time, relative),
                hours: getHour(time) - getHour(relative),
                minutes: getMinute(time) - getMinute(relative),
                seconds: getSecond(time) - getSecond(relative),
                milliseconds: getMillisecond(time) - getMillisecond(relative),
            };
        }
        else {
            let milliseconds = epoch(time, "milliseconds") - epoch(relative, "milliseconds");
            const sign = Math.sign(milliseconds);
            milliseconds = Math.abs(milliseconds);
            result = {};
            switch (greatestUnit) {
                case "hours":
                    result.hours = sign * Math.floor(milliseconds / (3600 * 1000));
                    milliseconds -= sign * result.hours * 3600 * 1000;
                case "minutes":
                    result.minutes = sign * Math.floor(milliseconds / (60 * 1000));
                    milliseconds -= sign * result.minutes * 60 * 1000;
                case "seconds":
                    result.seconds = sign * Math.floor(milliseconds / 1000);
                    milliseconds -= sign * result.seconds * 1000;
                case "milliseconds":
                    result.milliseconds = sign * milliseconds;
            }
        }
        return result;
    }
    DateTime.span = span;
    DateTime.epochStart = "0000-01-01T00:00:00.000Z";
    DateTime.epochEnd = "9999-12-31T23:59:59.999Z";
    function invert(time) {
        return `${Date.invert(getDate(time))}T${(24 - getHour(time)).toFixed(0).padStart(2, "0")}:${(60 - getMinute(time))
            .toFixed(0)
            .padStart(2, "0")}:${(60 - getSecond(time)).toFixed(0).padStart(2, "0")}.${(999 - getMillisecond(time))
            .toFixed(0)
            .padStart(3, "0")}Z`;
    }
    DateTime.invert = invert;
})(DateTime || (DateTime = {}));
//# sourceMappingURL=index.js.map